import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Bell, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  CreditCard, 
  Palmtree, 
  ListTodo,
  FileText,
  User,
  X,
  Volume2,
  VolumeX
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = window.location.origin;

// Notification sound (base64 encoded short beep)
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQgAj9markup3qYBAAA==';

const NotificationPopup = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const [dismissedIds, setDismissedIds] = useState([]); // Track dismissed notification IDs
  const audioRef = useRef(null);
  const checkIntervalRef = useRef(null);

  const token = localStorage.getItem('token');

  // Play notification sound
  const playSound = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      // Create audio context for better browser support
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContext();
      
      // Create oscillator for beep sound
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.5);
      
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.5);
    } catch (error) {
      console.log('Audio playback failed:', error);
    }
  }, [soundEnabled]);

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const browserNotif = new Notification('Maris CRM - Yeni Bildirim', {
        body: notification.message,
        icon: '/logo192.png',
        tag: notification.id,
        requireInteraction: true
      });

      browserNotif.onclick = () => {
        window.focus();
        if (notification.link) {
          navigate(notification.link);
        }
        browserNotif.close();
      };
    }
  }, [navigate]);

  // Check for new notifications
  const checkNotifications = useCallback(async () => {
    if (!token) return;

    try {
      const params = lastCheckedAt ? `?since=${encodeURIComponent(lastCheckedAt)}` : '?unread_only=true';
      const res = await fetch(`${API_URL}/api/notifications/urgent${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        
        if (data.notifications && data.notifications.length > 0) {
          // Filter out already shown/dismissed notifications
          const shownIds = notifications.map(n => n.id);
          const newNotifications = data.notifications.filter(
            n => !shownIds.includes(n.id) && !n.is_read && !dismissedIds.includes(n.id)
          );
          
          if (newNotifications.length > 0) {
            // Play sound and show popup for each new notification
            newNotifications.forEach((notif, index) => {
              setTimeout(() => {
                playSound();
                showBrowserNotification(notif);
              }, index * 1000); // Stagger sounds
            });

            // Show popup with first notification
            setCurrentNotification(newNotifications[0]);
            setNotifications(prev => [...newNotifications, ...prev].slice(0, 10));
            setShowPopup(true);
          }
        }

        setLastCheckedAt(new Date().toISOString());
      }
    } catch (error) {
      console.error('Error checking notifications:', error);
    }
  }, [token, lastCheckedAt, notifications, dismissedIds, playSound, showBrowserNotification]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Handle notification action
  const handleAction = (notification) => {
    markAsRead(notification.id);
    setShowPopup(false);
    
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Dismiss notification
  const handleDismiss = () => {
    if (currentNotification) {
      markAsRead(currentNotification.id);
      // Remove dismissed notification from list
      setNotifications(prev => prev.filter(n => n.id !== currentNotification.id));
    }
    setShowPopup(false);
    setCurrentNotification(null);
  };

  // Get icon based on notification type
  const getIcon = (type) => {
    switch (type) {
      case 'task_assigned':
        return <ListTodo className="w-6 h-6 text-blue-500" />;
      case 'payment_request':
      case 'payment_approved':
      case 'payment_rejected':
        return <CreditCard className="w-6 h-6 text-green-500" />;
      case 'leave_request':
      case 'leave_approved':
      case 'leave_rejected':
        return <Palmtree className="w-6 h-6 text-orange-500" />;
      case 'request_assigned':
      case 'request_updated':
        return <FileText className="w-6 h-6 text-purple-500" />;
      case 'message':
      case 'group_message':
        return <Bell className="w-6 h-6 text-pink-500" />;
      case 'meeting_invitation':
        return <Clock className="w-6 h-6 text-indigo-500" />;
      default:
        return <Bell className="w-6 h-6 text-cyan-500" />;
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500 animate-pulse';
      case 'high':
        return 'bg-orange-500';
      case 'medium':
        return 'bg-yellow-500';
      default:
        return 'bg-blue-500';
    }
  };

  // Initialize
  useEffect(() => {
    requestNotificationPermission();
    
    // Initial check
    checkNotifications();

    // Set up polling interval (every 30 seconds)
    checkIntervalRef.current = setInterval(checkNotifications, 30000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  if (!currentNotification) return null;

  return (
    <Dialog open={showPopup} onOpenChange={setShowPopup}>
      <DialogContent className="max-w-md border-2 border-cyan-500 shadow-2xl shadow-cyan-500/20">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <div className={`p-2 rounded-full ${getPriorityColor(currentNotification.priority)}`}>
                <Bell className="w-5 h-5 text-white" />
              </div>
              <span>Yeni Bildirim</span>
            </DialogTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-gray-500"
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Notification Content */}
          <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div className="flex-shrink-0">
              {getIcon(currentNotification.type)}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">
                {currentNotification.title}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {currentNotification.message}
              </p>
              {currentNotification.from_user && (
                <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                  <User className="w-3 h-3" />
                  <span>{currentNotification.from_user}</span>
                </div>
              )}
            </div>
          </div>

          {/* Priority Badge */}
          {currentNotification.priority === 'urgent' && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span className="text-sm font-medium text-red-700">
                Bu bildirim acil aksiyon gerektiriyor!
              </span>
            </div>
          )}

          {/* Notification Count */}
          {notifications.length > 1 && (
            <div className="text-center text-sm text-gray-500">
              +{notifications.length - 1} daha bildirim bekliyor
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button 
              onClick={() => handleAction(currentNotification)}
              className="flex-1 bg-cyan-600 hover:bg-cyan-700"
            >
              {currentNotification.link ? 'Görüntüle' : 'Tamam'}
            </Button>
            <Button 
              variant="outline" 
              onClick={handleDismiss}
              className="flex-1"
            >
              Kapat
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationPopup;
